import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiClock, FiTrendingUp, FiFileText, FiChevronRight } from 'react-icons/fi';
import InstructorDashboard from './InstructorDashboard';
import ActiveAttendanceSessions from './ActiveAttendanceSessions';
import DashboardAnnouncement from './DashboardAnnouncement';
import ManagedCoursesWidget from './ManagedCoursesWidget';
import StudentDashboardMessages from '../studentMessages/StudentDashboardMessages';
import SessionQnaPanel from '../sessionQna/SessionQnaPanel';
import { extractWeekNumber } from './activeAttendanceSessionsHelpers';
import ActivePollWidget from './ActivePollWidget';
import styles from './Dashboard.module.css';
import { useDashboard } from './useDashboard';
import DashboardSummaryCards from './dashboard/DashboardSummaryCards';

// Helper function to get stat icon class
const getStatIconClass = (color) => {
  switch (color) {
    case 'primary': return styles.statIcon + ' ' + styles.primary;
    case 'success': return styles.statIcon + ' ' + styles.success;
    case 'warning': return styles.statIcon + ' ' + styles.warning;
    default: return styles.statIcon + ' ' + styles.primary;
  }
};

const formatDashboardDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });



function Dashboard() {
  const {
    assignmentSummary,
    enrolledCourses,
    formatDueDate,
    getAssignmentStatus,
    getQuizStatus,
    getSurveyStatus,
    loaded,
    navigate,
    notesSummary,
    quizSummary,
    recentActivities,
    recentAssignments,
    recentNotes,
    recentQuizzes,
    recentSurveys,
    setShowPreviousCourses,
    showPreviousCourses,
    stats,
    surveySummary,
    taskCounters,
    user,
  } = useDashboard();
  const [qnaSession, setQnaSession] = useState(null);

  if (user?.role === 'instructor') {
    return <InstructorDashboard />;
  }


  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const openAssignmentInline = (assignmentId, weekNumber = null) => {
    if (!assignmentId) {
      return;
    }
    let url = `/assignments?assignmentId=${encodeURIComponent(assignmentId)}`;
    if (weekNumber) {
      url += `&weekNumber=${encodeURIComponent(weekNumber)}`;
    }
    navigate(url);
  };

  const handleAssignmentClick = (assignmentId, weekNumber = null) => {
    openAssignmentInline(assignmentId, weekNumber);
  };

  const handleQuizClick = (assignmentId) => {
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  const handleSurveyClick = (assignmentId) => {
    navigate(`/student/surveys/${assignmentId}`);
  };

  // Active Courses / Pending Tasks / Completed Tasks are counted server-side
  // now (/api/dashboard/task-counters). They used to be computed here and,
  // separately, in useSidebar -- and the two had drifted: this copy excluded
  // extra-credit weeks and ended courses, the sidebar's counted both.
  const counters = taskCounters || {
    courses: Number(stats?.totalCourses) || 0,
    pending: 0,
    completed: 0,
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerEyebrow}>{formatDashboardDate()}</div>
        <h1 className={styles.headerTitle}>
          <span dir="ltr">{user?.first_name}</span>
          {' '}
          {user?.role === 'instructor' && <span dir="rtl">شيخ </span>}
          <span dir="rtl">السلام عليكم</span>
        </h1>
        <p className={styles.headerSubtitle} style={{ marginTop: '1.5rem' }}>Here's what's happening with your courses today.</p>
      </div>

      {/* Sitewide announcement - Only show for students */}
      {user?.role === 'student' && <StudentDashboardMessages />}
      {user?.role === 'student' && <DashboardAnnouncement />}

      {/* Live Poll - Only show for students, pushed live over Socket.IO */}
      {user?.role === 'student' && <ActivePollWidget />}

      {/* Active Attendance Sessions - Only show for students */}
      {user?.role === 'student' && <ActiveAttendanceSessions onAskQuestion={setQnaSession} />}

      {/* Session Q&A - its own independent section, not nested in the
          Active Sessions card, opened via that card's Ask a Question button */}
      {user?.role === 'student' && (
        <SessionQnaPanel
          open={Boolean(qnaSession)}
          onClose={() => setQnaSession(null)}
          sessionId={qnaSession?.session_id}
          role="student"
          title={qnaSession ? `Q&A · ${extractWeekNumber(qnaSession.title) ? `Week ${extractWeekNumber(qnaSession.title)}` : (qnaSession.course_code || qnaSession.elective_code || qnaSession.title || '')}` : ''}
        />
      )}

      {/* Courses with granted permissions */}
      <ManagedCoursesWidget />

      {/* Gated on `loaded`, not just rendered early with empty contents.
          Measured on production: the cards used to mount as header-only stubs
          (topRow 184px, activity 113px) and then snap to full size ~180ms
          later, growing the page ~730px in one step -- a small version of the
          page followed by the real one, which is what read as loading twice.
          Rendering the body once, complete, is the only thing that removes
          that second pass; the greeting header still paints immediately. */}
      {/* My Assignments and My Quizzes Summary Sections - Only show for students */}
      {user?.role === 'student' && loaded && (
        <DashboardSummaryCards
          assignmentSummary={assignmentSummary}
          enrolledCourses={enrolledCourses}
          formatDueDate={formatDueDate}
          getAssignmentStatus={getAssignmentStatus}
          getQuizStatus={getQuizStatus}
          getSurveyStatus={getSurveyStatus}
          handleAssignmentClick={handleAssignmentClick}
          handleCourseClick={handleCourseClick}
          handleQuizClick={handleQuizClick}
          handleSurveyClick={handleSurveyClick}
          loaded={loaded}
          navigate={navigate}
          notesSummary={notesSummary}
          quizSummary={quizSummary}
          recentAssignments={recentAssignments}
          recentNotes={recentNotes}
          recentQuizzes={recentQuizzes}
          recentSurveys={recentSurveys}
          setShowPreviousCourses={setShowPreviousCourses}
          showPreviousCourses={showPreviousCourses}
          surveySummary={surveySummary}
          user={user}
        />
      )}

      {/* Stats Strip - Unified Module - Only show for students */}
      {user?.role === 'student' && loaded && (
        <motion.div
          className={`${styles.summaryCard} ${styles.statsStrip}`}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.statsStripContent}>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('primary')}>
                <FiBookOpen size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{counters.courses}</div>
                <div className={styles.statLabel}>Active Courses</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('warning')}>
                <FiClock size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{counters.pending}</div>
                <div className={styles.statLabel}>Pending Tasks</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('success')}>
                <FiTrendingUp size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{counters.completed}</div>
                <div className={styles.statLabel}>Completed Tasks</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Activity - Only show for students */}
      {user?.role === 'student' && loaded && (
        <motion.div
          className={`${styles.summaryCard} ${styles.activityCard}`}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.summaryHeader}>
            <h3 className={styles.summaryTitle}>
              <FiClock size={20} />
              Recent Activity
            </h3>
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryItems}>
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => {
                  // Map activity types to appropriate icons
                  const getActivityIcon = (type) => {
                    switch (type) {
                      case 'assignment':
                        return <FiFileText size={16} />;
                      case 'course':
                        return <FiBookOpen size={16} />;
                      case 'grade':
                        return <FiTrendingUp size={16} />;
                      default:
                        return <FiClock size={16} />;
                    }
                  };
                  
                  // Determine navigation target for activity
                  const handleActivityClick = () => {
                    if (activity.type === 'course' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else if (activity.type === 'assignment' && activity.assignment_id) {
                      openAssignmentInline(activity.assignment_id);
                    } else if (activity.type === 'grade' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else {
                      navigate('/dashboard');
                    }
                  };
                  
                  const hasNavigation = (activity.type === 'course' && activity.course_id) ||
                                       (activity.type === 'assignment' && activity.assignment_id) ||
                                       (activity.type === 'grade' && activity.course_id);
                  
                  return (
                    <div 
                      key={activity.id || `activity-${index}`} 
                      className={hasNavigation ? styles.listRow : styles.summaryItem}
                      onClick={hasNavigation ? handleActivityClick : undefined}
                      role={hasNavigation ? "button" : undefined}
                      tabIndex={hasNavigation ? 0 : undefined}
                      aria-label={hasNavigation ? `${activity.title}, ${activity.time}` : undefined}
                      onKeyDown={hasNavigation ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleActivityClick();
                        }
                      } : undefined}
                    >
                      <div className={styles.activityIcon}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className={styles.rowMain}>
                        <div className={styles.summaryItemTitle}>{activity.title}</div>
                        <div className={styles.rowMeta}>{activity.time}</div>
                      </div>
                      {hasNavigation && (
                        <div className={styles.rowChevron}>
                          <FiChevronRight size={16} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : loaded ? (
                <div className={styles.emptySummary}>
                  <FiClock />
                  <p>No recent activity.</p>
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      )}

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}
    </div>
  );
}

export default Dashboard;
